import { makeStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import RoundedButton from 'components/Shared/RoundedButton';
import Modal from 'components/Shared/Modal';
import { useSelector } from 'react-redux';
import WarningIcon from '@material-ui/icons/Warning';

const useStyles = makeStyles((theme) => ({
  modal: {
    width: '100%',
    minWidth: 480,
    maxWidth: '90vw',
    height: 'auto',
    maxHeight: '80vh',
    borderRadius: 16,
    boxShadow: '0 24px 48px rgba(0, 0, 0, 0.12)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    alignItems: 'center',
  },
  content: {
    flex: 1,
    padding: theme.spacing(4, 3),
    background: 'linear-gradient(135deg, #fafafa 0%, #ffffff 100%)',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2), // Reduce padding on small screens
    },
    '@media (max-height: 700px)': {
      padding: theme.spacing(2), // Reduce padding on low-height screens
    },
  },
  warningIcon: {
    fontSize: 64,
    color: '#f56565',
    marginBottom: theme.spacing(2),
    filter: 'drop-shadow(0 4px 8px rgba(245, 101, 101, 0.2))',
    '@media (max-height: 700px)': {
      display: 'none', // Hide icon on screens with height less than 700px
    },
  },
  title: {
    fontSize: 24,
    fontWeight: 600,
    color: '#2d3748',
    marginBottom: theme.spacing(2),
    lineHeight: 1.3,
    [theme.breakpoints.down('sm')]: {
      fontSize: 20, // Reduce title size on small screens
      marginBottom: theme.spacing(1.5),
    },
    '@media (max-height: 700px)': {
      fontSize: 20, // Reduce title size on low-height screens
      marginBottom: theme.spacing(1),
    },
  },
  message: {
    fontSize: 16,
    color: '#4a5568',
    lineHeight: 1.6,
    marginBottom: theme.spacing(1),
    maxWidth: 400,
  },
  warningMessage: {
    fontSize: 14,
    color: '#e53e3e',
    fontWeight: 500,
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    background: 'rgba(245, 101, 101, 0.08)',
    borderRadius: 8,
    border: '1px solid rgba(245, 101, 101, 0.2)',
    maxWidth: 400,
  },
  criticalWarning: {
    fontSize: 14,
    color: '#c53030',
    fontWeight: 600,
    marginTop: theme.spacing(2),
    padding: theme.spacing(2.5),
    background: 'rgba(197, 48, 48, 0.12)',
    borderRadius: 8,
    border: '2px solid rgba(197, 48, 48, 0.3)',
    maxWidth: 400,
    boxShadow: '0 4px 12px rgba(197, 48, 48, 0.15)',
  },
  accountName: {
    fontWeight: 600,
    color: '#2b6cb0',
  },
  actionsContainer: {
    padding: theme.spacing(3, 6),
    background: '#ffffff',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    borderRadius: '0 0 16px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexShrink: 0,
    boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.08)',
    width: '100%',
  },
  actionButton: {
    textTransform: 'uppercase',
    borderRadius: 10,
    padding: theme.spacing(1.5, 3),
    letterSpacing: 1.5,
    fontWeight: 600,
    fontSize: 14,
    minWidth: 100,
    height: 40,
  },
  removeButton: {
    backgroundColor: '#dc3545',
    borderColor: '#dc3545',
    color: 'white',
    '&:hover': {
      backgroundColor: '#c82333',
      borderColor: '#bd2130',
    },
    '&:disabled': {
      backgroundColor: '#6c757d',
      borderColor: '#6c757d',
    },
  },
}));

const RemoveAccountModal = ({ open, handleClose, accountName, onConfirm, isRemoving, isLastAccount = false }) => {
  const classes = useStyles();
  const { en: { btn, titles } } = useSelector((state) => state.i18n);

  return (
    <Modal
      open={open}
      classes={{ paper: classes.modal }}
      title={titles.removeAccess}
      handleClose={handleClose}
    >
      <div className={classes.content}>
        <WarningIcon className={classes.warningIcon} />
        <h2 className={classes.title}>
          {titles.removeAccessConfirmation}
        </h2>
        <p className={classes.message}>
          {titles.areYouSureRemoveAccess}<br />
          <span className={classes.accountName}>"{accountName}"</span>?
        </p>
        {!isLastAccount && (
          <div className={classes.warningMessage}>
            {titles.warningPermissionsLost}
          </div>
        )}
        {isLastAccount && (
          <div className={classes.criticalWarning}>
            ⚠️ <strong>CRITICAL WARNING:</strong> This is the last account for this user.
            Removing it will make the user disappear from the users list completely.
            The user will lose all access to the system.
          </div>
        )}
      </div>

      <div className={classes.actionsContainer}>
        <RoundedButton
          variant="outlined"
          color="primary"
          size="medium"
          onClick={handleClose}
          disabled={isRemoving}
          className={classes.actionButton}
        >
          {btn.cancel || 'Cancel'}
        </RoundedButton>
        <RoundedButton
          variant="contained"
          size="medium"
          onClick={onConfirm}
          isLoading={isRemoving}
          className={`${classes.actionButton} ${classes.removeButton}`}
        >
          {titles.removeAccess}
        </RoundedButton>
      </div>
    </Modal>
  );
};

RemoveAccountModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  accountName: PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isRemoving: PropTypes.bool,
  isLastAccount: PropTypes.bool,
};

RemoveAccountModal.defaultProps = {
  isRemoving: false,
  isLastAccount: false,
};

export default RemoveAccountModal;
